import React, { useEffect, useRef } from "react";
import { astryx, Check, ScriptSkeleton } from "@shadowcue/ui";

interface GenerationTraceProps {
  /** Index of the step in progress; drives how far the outline has built. */
  stepIndex: number;
  /** The line under the outline: the step in progress, or the result. */
  statusMessage: string;
  isDone: boolean;
  /** Regenerating reworks an outline that already exists instead of building one. */
  isRevising: boolean;
}

const GenerationTrace = ({
  stepIndex,
  statusMessage,
  isDone,
  isRevising,
}: GenerationTraceProps): React.ReactElement => {
  // Starting a generation removes the prompt field and the button that was
  // pressed, which would drop keyboard focus onto the page behind the dialog.
  // Move it to the dialog title instead: Astryx's DialogHeader renders it as
  // a focusable <h2 tabIndex={-1}> for exactly this, without a focus ring,
  // and it now reads "Drafting your script…" for screen readers.
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    containerRef.current
      ?.closest("dialog")
      ?.querySelector<HTMLElement>("h2[tabindex='-1']")
      ?.focus();
  }, []);

  return (
    <div ref={containerRef}>
      <astryx.VStack gap={3}>
        <ScriptSkeleton
          stage={stepIndex}
          isDone={isDone}
          variant={isRevising ? "revise" : "build"}
        />

        {/* Visual only: the dialog's live region announces the status, so the
            spinner's own "Loading" status would just talk over it. */}
        <astryx.HStack gap={2} align="center">
          {isDone ? (
            <astryx.Icon icon={Check} size="sm" />
          ) : (
            <astryx.Spinner size="sm" shade="subtle" aria-hidden="true" />
          )}
          <astryx.Text type="supporting" color="secondary">
            {statusMessage}
          </astryx.Text>
        </astryx.HStack>
      </astryx.VStack>
    </div>
  );
};

export default GenerationTrace;
